import { Button, ButtonDropdown, Dialog, LoadMoreButton, Notification, Spinner } from '@rio-cloud/rio-uikit';
import { useEffect, useState } from 'react';
import { RestrictArea } from '../../../../../../components/restrictArea';
import { Form } from '../form';
import { deleteMatrizSupport, fetchMatrizSupport } from '../../../../../../api/tas';
import { GroupsMatriz, optionsGroupsMatriz, subTopicSupport, typeSubjectOccorence } from '../../../../../support/constants';
import { useIntl } from 'react-intl';

export interface IResponseData {
    id: number;
    subject: string;
    subtopic: string;
    groupIds: string[]
}

const MatrizUserListContent = () => {
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userHasAccess, setUserHasAccess] = useState(true);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [pageSize, setPageSize] = useState(20);
    const [totalElements, setTotalElements] = useState(0);
    const [data, setData] = useState<IResponseData[]>([]);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [idDelete, setIdDelete] = useState<null | number>(null);
    const [matrizDelete, setMatrizDelete] = useState<IResponseData | null>(null);
    const intl = useIntl();

    const fetchList = async () => {
        try {
            setLoading(true);
            const params = {
                pageSize,
            };

            const response = await fetchMatrizSupport(params);
            if (!response.content) {
                throw new Error('NO_CONTENT')
            }

            setData(response.content)
            setLoading(false);
            setTotalElements(response.totalElements);
            setPageSize(response.numberOfElements);
        } catch (error) {
            setData([]);
        } finally {
            setLoading(false);
        }
    }

    const handleFormClose = () => {
        setShowForm(false)
    };

    const handleDelete = (id: number) => {
        setIdDelete(id);
        setShowDeleteDialog(true);
    }

    const confirmDelete = async () => {
        if (!idDelete) {
            Notification.error('Não foi possivel excluir')
            return;
        };
        setDeleteLoading(true);
        try {

            await deleteMatrizSupport(idDelete);
            Notification.success('Matriz excluida com sucesso')
        } catch (error) {
            Notification.error('Não foi possivel excluir')
        }
    }

    const onLoadMore = () => {
        const nextSize = pageSize + 30;

        setPageSize(nextSize > totalElements ? totalElements : nextSize);
    }


    if (!userHasAccess) {
        return <RestrictArea showBackButton={false} />;
    }

    useEffect(() => {
        fetchList()
    }, [pageSize])


    return loading ? (
        <div className="flex justify-center items-center h-64">
            <Spinner />
        </div>
    ) : (
        <div className="p-6 max-w-[1000px] mx-auto">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <div className="text-size-h2">Matriz RACI</div>
                </div>
                <Button
                    bsStyle={Button.PRIMARY}
                    onClick={() => setShowForm(true)}
                    iconName="rioglyph-plus-sign"
                >
                    Criar
                </Button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div>
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left font-medium text-gray-500">
                                    Assunto
                                </th>
                                <th className="px-6 py-3 text-left font-medium text-gray-500">
                                    Tema
                                </th>
                                <th className="px-6 py-3 text-left font-medium text-gray-500">
                                    Grupos
                                </th>
                                <th className="px-6 py-3 text-right font-medium text-gray-500"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {data?.map((d) => (
                                <tr key={d.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-gray-900">
                                            {typeSubjectOccorence[d?.subject as keyof typeof typeSubjectOccorence]}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-gray-900 text-capitalize">{subTopicSupport[d?.subtopic as keyof typeof subTopicSupport]}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {d.groupIds.map((res) => <div key={res} className="text-gray-900">{GroupsMatriz[res as keyof typeof GroupsMatriz]}</div>)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                                        <div className="flex justify-end space-x-2">
                                            <ButtonDropdown
                                                title={<span className='rioglyph rioglyph-option-vertical' />}
                                                items={[
                                                    {
                                                        value: 'Editar',
                                                        onSelect: () => {
                                                            setMatrizDelete(d);
                                                            setShowForm(true);
                                                        }
                                                    },
                                                    {
                                                        value: 'Excluir',
                                                        onSelect: () => handleDelete(d?.id)
                                                    }
                                                ]}
                                                bsStyle='muted'
                                                pullRight
                                                noCaret
                                                usePortal
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {data.length === 0 && (
                    <div className="text-center py-12">
                        <div className="text-gray-500">
                            <p className="font-medium mb-2">Sem resultado</p>
                        </div>
                    </div>
                )}
            </div>

            <LoadMoreButton
                loaded={pageSize}
                total={totalElements}
                onLoadMore={onLoadMore}
                loadMoreMessage={intl.formatMessage({ id: "intl-msg:monitoring.loadMore" })}
                noMoreMessage={intl.formatMessage({ id: "intl-msg:monitoring.allLoaded" })}
            />

            {showForm && (
                <Dialog
                    show={showForm}
                    onClose={handleFormClose}
                    showCloseButton={true}
                    title="Criar a matriz"
                    bsSize={Dialog.SIZE_SM}
                    body={(
                        <Form.UserMatrizForm
                            data={matrizDelete || undefined}
                            onCancel={() => { setShowForm(false) }}
                            onSuccess={() => { setShowForm(false) }}
                        />
                    )}
                />
            )}

            <Dialog
                show={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                title="Confirmar Exclusão"
                bsSize={Dialog.SIZE_SM}
            >
                <div className="p-6">
                    <p className="text-size-h4 text-gray-700 mb-4">
                        Tem certeza que deseja excluir a matriz?

                    </p>
                    <p className="text-gray-600 mb-6">
                        Esta ação não pode ser desfeita.
                    </p>
                    <div className="flex justify-end space-x-3">
                        <Button
                            bsStyle={Button.DEFAULT}
                            onClick={() => setShowDeleteDialog(false)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            bsStyle={Button.DANGER}
                            onClick={confirmDelete}
                            disabled={deleteLoading}
                            iconName={deleteLoading ? "rioglyph-spinner" : undefined}
                        >
                            {deleteLoading ? "Excluindo..." : "Excluir"}
                        </Button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

const MatrizUserList = (props: any) => {
    return (
        <MatrizUserListContent {...props} />
    );
};

export default MatrizUserList;

